import React, { useMemo } from 'react';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { scribeTokens } from './tokens';

export function createScribeTheme(parent, tokens = scribeTokens) {
  const { colors, typography, radius, controlHeight } = tokens;
  return createTheme(parent, {
    scribe: tokens,
    palette: {
      primary: { main: colors.brand, dark: colors.brandHover, light: colors.brandSoft, contrastText: colors.surface },
      secondary: { main: colors.accent, dark: colors.accentHover, light: colors.accentSoft, contrastText: colors.surface },
      error: { main: colors.danger },
      text: { primary: colors.text, secondary: colors.secondary, disabled: colors.disabledText },
      divider: colors.border,
      background: { default: colors.canvas, paper: colors.surface },
      action: { disabled: colors.disabledText, disabledBackground: colors.disabled, hover: colors.brandSoft },
    },
    typography: {
      fontFamily: tokens.fontFamily,
      allVariants: { color: colors.text },
      h1: typography.page, h2: typography.heading, h3: typography.section,
      h4: typography.section, h5: typography.section, h6: typography.section,
      body1: typography.body, body2: typography.body, subtitle1: typography.section,
      subtitle2: typography.label, caption: typography.helper,
      button: { ...typography.label, textTransform: 'none' },
    },
    shape: { borderRadius: radius.control },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { ...typography.label, borderRadius: `${radius.control}px`, minHeight: controlHeight.normal, padding: '8px 16px', textTransform: 'none' }, sizeSmall: { minHeight: controlHeight.compact, padding: '4px 12px' } },
      },
      MuiOutlinedInput: { styleOverrides: { root: { ...typography.body, borderRadius: `${radius.control}px`, backgroundColor: colors.surface, '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border } }, inputSizeSmall: { padding: '10px 12px' } } },
      MuiInputLabel: { styleOverrides: { root: { ...typography.body, color: colors.secondary } } },
      MuiSelect: { styleOverrides: { select: { ...typography.body, minHeight: '20px' } } },
      MuiIconButton: { styleOverrides: { root: { color: colors.brand, '&:hover': { backgroundColor: colors.brandSoft }, '&.Mui-focusVisible': { outline: `2px solid ${colors.brand}`, outlineOffset: 2 } }, sizeSmall: { width: controlHeight.compact, height: controlHeight.compact } } },
      MuiTab: { styleOverrides: { root: { ...typography.label, textTransform: 'none' } } },
      MuiChip: { styleOverrides: { root: { ...typography.helper, fontWeight: 600 } } },
      MuiTooltip: { styleOverrides: { tooltip: typography.helper } },
    },
  });
}

export default function ScribeThemeProvider({ children, tokens = scribeTokens }) {
  const parent = useTheme();
  const theme = useMemo(() => createScribeTheme(parent, tokens), [parent, tokens]);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
